import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useLocation } from "react-router-dom";
import { api, inr, num, fmtDate, formatApiError, API_URL } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "./Quotations";
import { Plus, FileText, Trash2, Pencil, MessageCircle, FlaskConical, PackageX, ReceiptText, IndianRupee, Paperclip, MailWarning, Mail, FolderOpen } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { InvoiceAiEmailDialog } from "@/components/QuoteAiEmailDialog";
import { toast } from "sonner";

const emptyForm = { customer_id: "", sales_order_id: "", schedule_id: "", date: "", due_date: "", items: [], freight: 0,
  transport: "", transporter_id: "", eway_bill_number: "",
  from_pincode: "401202", to_pincode: "", distance_km: 0, eway_validity_days: 0 };

const deriveHsn = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("reinforced")) return "40092100";
  if (["tube", "hose", "profile", "strip", "sleeve", "sleeving"].some((k) => n.includes(k))) return "40091100";
  if (["o'ring", "o-ring", "oring", "o ring"].some((k) => n.includes(k))) return "40169990";
  return "";
};

export default function Invoices() {
  const [list, setList] = useState([]);
  const [invSearch, setInvSearch] = useUrlSearch();
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [distLoading, setDistLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [consInfo, setConsInfo] = useState(null);
  const [aiEmailFor, setAiEmailFor] = useState(null);

  const emptyPay = { received: false, amount: "", mode: "Bank Transfer", reference: "" };
  const [pay, setPay] = useState(emptyPay);
  const [advances, setAdvances] = useState([]);
  const advanceForSo = (soId) => {
    const so = orders.find((o) => o.id === soId);
    const cid = so?.customer_id || form.customer_id;
    return advances.filter((a) =>
      (soId && a.sales_order_id === soId) ||
      (so?.quotation_id && !a.sales_order_id && a.quotation_id === so.quotation_id) ||
      (cid && !a.sales_order_id && !a.quotation_id && a.customer_id === cid)
    ).reduce((s, a) => s + (+a.amount || 0), 0);
  };
  const location = useLocation();

  const load = () => Promise.all([
    api.get("/invoices").then((r) => setList(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
    api.get("/sales-orders").then((r) => setOrders(r.data)),
    api.get("/dispatch").then((r) => setDispatches(r.data)),
    api.get("/transporters").then((r) => setTransporters(r.data)),
    api.get("/payments").then((r) => setAdvances(r.data.filter((p) => p.kind === "advance" && !p.invoice_id))),
  ]);
  useEffect(() => { load(); }, []);

  const buildConsItems = (sum, schedId) => {
    let factor = null;
    if (schedId) {
      const s = (sum.schedule || []).find((x) => x.id === schedId);
      if (s && sum.total_qty > 0) factor = (+s.planned_qty || 0) / sum.total_qty;
    }
    return sum.per_item.filter((p) => p.remaining > 0).map((p) => ({
      product_id: p.product_id, product_code: p.product_code, description: p.description,
      color: p.color, size: p.size, tc_size: p.tc_size, cut_length: p.cut_length || "", unit: p.unit,
      quantity: factor !== null ? Math.min(Math.round(p.ordered * factor * 100) / 100, p.remaining) : p.remaining,
      unit_price: p.unit_price, gst: p.gst, purchase_rate: p.purchase_rate || 0,
      hsn: p.hsn || deriveHsn(p.description), _max: p.remaining }));
  };

  const applyBatch = (sid) => {
    if (!consInfo) return;
    const v = sid === "none" ? "" : sid;
    setForm((f) => ({ ...f, schedule_id: v, items: buildConsItems(consInfo, v) }));
  };

  const [retInv, setRetInv] = useState(null);
  const [retReopen, setRetReopen] = useState(true);
  const [retBusy, setRetBusy] = useState(false);
  const confirmReturn = async () => {
    setRetBusy(true);
    try {
      const r = await api.post(`/invoices/${retInv.id}/return`, { reopen_so: retReopen });
      toast.success(`Invoice ${retInv.number} marked Returned — ${retReopen ? "Sales Order reopened for re-dispatch" : "Sales Order closed (no replacement will be sent)"}`);
      if (r.data?.email_sent) toast.success(`Credit Note ${r.data.credit_note_number || ""} emailed to ${r.data.email_to} with PDF attached`);
      else toast.warning("Credit Note email could not be sent (no email on file or SMTP issue) — you can download and send it manually");
      if (!retReopen) {
        setVcn({ number: "", amount: retInv.total || "" });
        setVcnFile(null); setVcnDone(null);
        setVcnNeedsBill(false); setVcnBill({ number: "", amount: "" }); setVcnBillFile(null);
        setVcnInv({ ...retInv, credit_note_number: r.data?.credit_note_number });
      }
      setRetInv(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRetBusy(false);
  };

  const [vcnInv, setVcnInv] = useState(null);
  const [vcn, setVcn] = useState({ number: "", amount: "" });
  const [vcnFile, setVcnFile] = useState(null);
  const [vcnBusy, setVcnBusy] = useState(false);
  const [vcnDone, setVcnDone] = useState(null);
  const [vcnNeedsBill, setVcnNeedsBill] = useState(false);
  const [vcnBill, setVcnBill] = useState({ number: "", amount: "" });
  const [vcnBillFile, setVcnBillFile] = useState(null);
  const saveVcn = async () => {
    if (!(+vcn.amount > 0)) { toast.error("Enter the vendor credit note amount"); return; }
    if (vcnNeedsBill && !(+vcnBill.amount > 0)) { toast.error("Enter the vendor bill amount"); return; }
    setVcnBusy(true);
    try {
      const fd = new FormData();
      fd.append("cn_number", vcn.number.trim());
      fd.append("amount", +vcn.amount);
      if (vcnFile) fd.append("file", vcnFile);
      if (vcnNeedsBill) {
        fd.append("bill_number", vcnBill.number.trim());
        fd.append("bill_amount", +vcnBill.amount);
        if (vcnBillFile) fd.append("bill_file", vcnBillFile);
      }
      const r = await api.post(`/invoices/${vcnInv.id}/vendor-credit-note`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.needs_bill) {
        setVcnNeedsBill(true);
        setVcnBill({ number: "", amount: "" });
        toast.warning(`No vendor bill on record${r.data.vendor_name ? ` for ${r.data.vendor_name}` : ""} — enter the bill details below first`);
      } else {
        toast.success(`Vendor credit note ${r.data.debit_note_number} recorded${r.data.vendor_name ? ` — ${r.data.vendor_name}` : ""}`);
        if (r.data.email_sent) toast.success(`Debit Note emailed to vendor (${r.data.email_to}) with PDF attached`);
        else toast.warning("Debit Note email not sent — vendor has no email on file");
        setVcnDone(r.data);
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setVcnBusy(false);
  };
  const openCaDocs = async (i) => {
    try {
      const r = await api.get(`/invoices/${i.id}/return-docs`);
      setVcnNeedsBill(false); setVcnBill({ number: "", amount: "" }); setVcnBillFile(null);
      if (r.data.has_debit_note) { setVcnDone(r.data); setVcnInv(i); }
      else {
        setVcn({ number: "", amount: i.total || "" }); setVcnFile(null); setVcnDone(null); setVcnInv(i);
        toast.info("Vendor credit note not recorded yet — complete it to get the Debit Note for your CA");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const autoDue = (custId, dateStr) => {
    const t = String(customers.find((x) => x.id === custId)?.credit_terms || "").trim();
    const days = /^\d+$/.test(t) ? +t : 0;
    const base = dateStr ? new Date(dateStr) : new Date();
    base.setDate(base.getDate() + days);
    return base.toISOString().slice(0, 10);
  };

  const pickOrder = async (oid, schedId = "") => {
    const o = orders.find((x) => x.id === oid);
    if (!o) return;
    const dp = dispatches.find((d) => d.sales_order_id === oid);
    if (o.invoicing_type === "consignment") {
      try {
        const r = await api.get(`/consignments/summary/${oid}`);
        setConsInfo(r.data);
        const items = buildConsItems(r.data, schedId);
        setForm((f) => ({ ...f, sales_order_id: oid, customer_id: o.customer_id, schedule_id: schedId || "", items, freight: 0,
          due_date: f.due_date || autoDue(o.customer_id, f.date),
          transport: dp?.transporter || f.transport, transporter_id: dp?.transporter_id || f.transporter_id,
          eway_bill_number: dp?.eway_bill_number || f.eway_bill_number,
          to_pincode: o.customer_snapshot?.shipping_pincode || f.to_pincode }));
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    setConsInfo(null);
    setForm((f) => ({ ...f, sales_order_id: oid, customer_id: o.customer_id, schedule_id: "",
              due_date: f.due_date || autoDue(o.customer_id, f.date),
              items: o.items.map((i) => ({ ...i, hsn: i.hsn || deriveHsn(i.description) })), freight: o.freight,
              transport: dp?.transporter || f.transport, transporter_id: dp?.transporter_id || f.transporter_id,
              eway_bill_number: dp?.eway_bill_number || f.eway_bill_number,
              to_pincode: o.customer_snapshot?.shipping_pincode || f.to_pincode }));
  };

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const p = qp.get("so");
    const sc = qp.get("sched") || "";
    if (p && orders.length && !open && !editing) {
      openNew();
      setTimeout(() => pickOrder(p, sc), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const calcDistance = async () => {
    if (!/^\d{6}$/.test(form.from_pincode) || !/^\d{6}$/.test(form.to_pincode)) {
      toast.error("Enter valid 6-digit pincodes"); return;
    }
    setDistLoading(true);
    try {
      const r = await api.get("/utils/pin-distance", { params: { from_pin: form.from_pincode, to_pin: form.to_pincode } });
      setForm((f) => ({ ...f, distance_km: r.data.road_km_estimate, eway_validity_days: r.data.eway_validity_days }));
      toast.success(`≈ ${r.data.road_km_estimate} km — E-Way validity ${r.data.eway_validity_days} day(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    setDistLoading(false);
  };

  const totals = form.items.reduce((acc, it) => {
    const sub = (+it.quantity || 0) * (+it.unit_price || 0);
    acc.sub += sub; acc.gst += sub * (+it.gst || 0) / 100; return acc;
  }, { sub: 0, gst: 0 });
  const freightGst = (+form.freight || 0) * 0.18;
  const grand = totals.sub + totals.gst + freightGst + (+form.freight || 0);
  const invoicedSoIds = new Set(list.filter((i) => i.sales_order_id).map((i) => i.sales_order_id));
  const shownInv = list.filter((i) => {
    const s = invSearch.trim().toLowerCase();
    if (!s) return true;
    return [i.number, i.customer_snapshot?.company_name, i.eway_bill_number, i.status, i.transport]
      .some((f) => (f || "").toLowerCase().includes(s));
  });
  const pager = usePager(shownInv);
  const selectableOrders = orders.filter((o) =>
    (((o.invoicing_type === "consignment" ? !o.invoiced : !invoicedSoIds.has(o.id)) &&
      (!o.po_status || o.po_status === "Received")) ||
    (editing && form.sales_order_id === o.id)));

  const viewEwayFile = async (fid) => {
    try {
      const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open E-Way bill file"); }
  };

  const paymentLink = async (i) => {
    try {
      const r = await api.post(`/razorpay/payment-link/${i.id}`);
      const url = r.data.short_url;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
      if (r.data.mode === "test") {
        toast.warning("⚠ This link is in TEST mode — Razorpay LIVE keys are not active on this server", { duration: 10000 });
      }
      const snap = i.customer_snapshot || {};
      const mobile = (snap.mobile || "").replace(/\D/g, "");
      const msg = encodeURIComponent(
        `Dear ${snap.company_name || "Customer"},\nPlease find the secure payment link for Invoice ${i.number} (Balance: ${inr(r.data.amount)}):\n${url}\n\nUPI / Cards / Netbanking accepted.\n— Hi-Tech Silico`);
      toast.success(`Payment link copied — ${url}`, {
        duration: 10000,
        action: { label: "WhatsApp", onClick: () => window.open(`https://wa.me/${mobile}?text=${msg}`, "_blank") },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [remindBusy, setRemindBusy] = useState("");
  const sendReminder = async (i) => {
    setRemindBusy(i.id);
    try {
      const r = await api.post(`/invoices/${i.id}/send-reminder`);
      toast.success(`Overdue reminder emailed to ${r.data.email} — balance ${inr(r.data.balance)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setRemindBusy("");
  };

  const shareWhatsApp = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/share-link`);
      const link = `${API_URL}/public/invoices/${r.data.share_token}/pdf`;
      const name = inv.customer_snapshot?.company_name || "Customer";
      const msg = `Dear ${name},\n\nPlease find our Invoice ${inv.number} - ${name} for ${inr(inv.total)}.\nView / download the PDF here: ${link}\n\nRegards,\nHi-Tech Silico\n+91 8888837157`;
      const digits = (inv.customer_snapshot?.mobile || "").replace(/\D/g, "");
      const phone = digits.length === 10 ? `91${digits}` : digits;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    try {
      let items = form.items;
      if (consInfo && !editing) {
        items = items.filter((i) => +i.quantity > 0);
        if (items.length === 0) { toast.error("Enter a quantity for at least one item"); return; }
        for (const it of items) {
          if (it._max !== undefined && +it.quantity > it._max + 0.001) {
            toast.error(`Quantity for '${it.description}' exceeds remaining PO balance (${it._max})`); return;
          }
        }
      }
      const body = { ...form, freight: +form.freight || 0,
        distance_km: +form.distance_km || 0, eway_validity_days: +form.eway_validity_days || 0,
        items: items.map(({ _max, ...i }) => ({ ...i, quantity: +i.quantity, unit_price: +i.unit_price, gst: +i.gst })) };
      if (!body.date) delete body.date;
      if (!body.due_date) delete body.due_date;
      if (!body.sales_order_id) delete body.sales_order_id;
      if (!body.schedule_id) delete body.schedule_id;
      let created = null;
      if (editing) await api.put(`/invoices/${editing}`, body);
      else { const r = await api.post("/invoices", body); created = r.data; }
      if (created && pay.received && +pay.amount > 0) {
        try {
          await api.post("/payments", { invoice_id: created.id, amount: +pay.amount, mode: pay.mode, reference: pay.reference });
          toast.success(`Payment of ${inr(+pay.amount)} recorded against ${created.number}`);
        } catch (pe) { toast.error("Invoice saved but payment failed: " + formatApiError(pe)); }
      }
      setPay(emptyPay);
      toast.success(editing ? "Invoice updated" : consInfo ? `Consignment #${consInfo.consignments_count + 1} invoiced` : "Invoice created");
      setOpen(false); setEditing(null); setForm(emptyForm); setConsInfo(null); load();
      if (created) {
        toast.success("Sharing invoice on WhatsApp");
        shareWhatsApp(created);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [invNumber, setInvNumber] = useState("");

  const openNew = () => {
    setEditing(null); setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) }); setInvNumber(""); setConsInfo(null); setPay(emptyPay); setOpen(true);
    api.get("/invoices/next-number").then((r) => setInvNumber(r.data.number)).catch(() => {});
  };
  const edit = (inv) => {
    setEditing(inv.id);
    setInvNumber(inv.number);
    setConsInfo(null);
    setForm({ customer_id: inv.customer_id, sales_order_id: inv.sales_order_id || "",
              schedule_id: inv.schedule_id || "",
              date: inv.date ? String(inv.date).slice(0, 10) : "",
              due_date: inv.due_date ? String(inv.due_date).slice(0, 10) : "",
              items: inv.items?.length ? inv.items.map((i) => ({ ...i })) : [],
              freight: inv.freight || 0,
              transport: inv.transport || "", transporter_id: inv.transporter_id || "",
              eway_bill_number: inv.eway_bill_number || "",
              from_pincode: inv.from_pincode || "401202", to_pincode: inv.to_pincode || "",
              distance_km: inv.distance_km || 0, eway_validity_days: inv.eway_validity_days || 0 });
    setOpen(true);
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/invoices/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Tax invoices & receivables"
        right={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setConsInfo(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="inv-new"><Plus size={14} className="mr-1" /> New Invoice</Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader className="shrink-0"><DialogTitle>{editing ? "Edit" : "New"} Invoice</DialogTitle></DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pl-1 pr-4 space-y-0" data-testid="inv-dialog-body">
              {consInfo && (
                <div className="border border-indigo-200 bg-indigo-50 rounded-md px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-6 gap-y-1 mb-1" data-testid="inv-cons-strip">
                  <span className="text-[11px] uppercase tracking-widest text-indigo-600 font-semibold">Consignment Invoice #{consInfo.consignments_count + 1}</span>
                  <span>Total PO Qty: <b className="tabular-nums">{num(consInfo.total_qty)}</b></span>
                  <span>Already Invoiced: <b className="tabular-nums">{num(consInfo.invoiced_qty)}</b></span>
                  <span>Balance Available: <b className="tabular-nums text-indigo-700">{num(consInfo.pending_qty)}</b></span>
                  {!editing && (consInfo.schedule || []).some((s) => !s.invoice_number) && (
                    <span className="flex items-center gap-2 ml-auto">
                      <span className="text-[11px] uppercase tracking-wider text-indigo-600">Dispatch Batch:</span>
                      <Select value={form.schedule_id || "none"} onValueChange={applyBatch}>
                        <SelectTrigger className="h-8 text-xs w-56 bg-white" data-testid="inv-sched-batch"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Full balance —</SelectItem>
                          {(consInfo.schedule || []).filter((s) => !s.invoice_number).map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.planned_date ? fmtDate(s.planned_date) : "No date"} · {num(s.planned_qty)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-6 gap-3 mb-3">
                <div className="col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">From Sales Order (optional)</Label>
                  <Select value={form.sales_order_id || "none"} onValueChange={(v) => v !== "none" ? pickOrder(v) : setForm({ ...form, sales_order_id: "" })}>
                    <SelectTrigger className="mt-1.5" data-testid="inv-so"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {selectableOrders.map((o) => <SelectItem key={o.id} value={o.id}>{o.number} · {o.customer_snapshot?.company_name}</SelectItem>)}
                      {selectableOrders.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No open sales orders — already-invoiced orders are hidden</div>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, due_date: editing ? form.due_date : autoDue(v, form.date) })}>
                    <SelectTrigger className="mt-1.5" data-testid="inv-customer"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Invoice Date</Label>
                  <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v, due_date: editing || !form.customer_id ? form.due_date : autoDue(form.customer_id, v) })} className="mt-1.5" testId="inv-date" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Due Date (payment)</Label>
                  <DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} className="mt-1.5" testId="inv-due" />
                  {!editing && form.customer_id && (() => {
                    const t = String(customers.find((c) => c.id === form.customer_id)?.credit_terms || "Advance");
                    return (
                      <p className="text-[10px] text-slate-400 mt-1" data-testid="inv-due-hint">
                        Auto-set from customer credit terms: {t === "Advance" ? "Advance Payment (due on invoice date)" : `${t} days credit`} — editable
                      </p>
                    );
                  })()}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Transporter</Label>
                  <Select value={form.transporter_id} onValueChange={(v) => {
                    const t = transporters.find((x) => x.id === v);
                    setForm({ ...form, transporter_id: v, transport: t?.name || "" });
                  }}>
                    <SelectTrigger className="mt-1.5" data-testid="inv-transporter"><SelectValue placeholder="Select transporter" /></SelectTrigger>
                    <SelectContent>
                      {transporters.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      {transporters.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No transporters — add under Transporters</div>}
                    </SelectContent>
                  </Select>
                  {form.transport && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      {form.transport}
                      {(() => { const t = transporters.find((x) => x.id === form.transporter_id); return t?.gstin ? <span className="ml-1 font-mono text-slate-700" data-testid="inv-transporter-gstin">· GSTIN: {t.gstin}</span> : null; })()}
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">E-Way Bill No.</Label>
                  <Input value={form.eway_bill_number} onChange={(e) => setForm({ ...form, eway_bill_number: e.target.value })} className="mt-1.5" data-testid="inv-eway" />
                </div>
                <div className="col-span-6 border border-slate-200 rounded-md p-3">
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Pin-to-Pin Distance (E-Way Bill)</p>
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">From Pincode</Label>
                      <Input value={form.from_pincode} onChange={(e) => setForm({ ...form, from_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="mt-1 h-10" data-testid="inv-from-pin" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">To Pincode</Label>
                      <Input value={form.to_pincode} onChange={(e) => setForm({ ...form, to_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="mt-1 h-10" data-testid="inv-to-pin" />
                    </div>
                    <Button type="button" variant="outline" onClick={calcDistance} disabled={distLoading} className="h-10" data-testid="inv-calc-dist">
                      {distLoading ? "..." : "Calculate"}
                    </Button>
                    <div className="text-sm text-slate-700 pb-2" data-testid="inv-dist-result">
                      {form.distance_km > 0 && <><b>{form.distance_km} km</b> · validity {form.eway_validity_days}d</>}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Approximate road distance. E-Way validity: 1 day per 200 km. Verify on the E-Way portal.</p>
                </div>

                {!editing && (
                  <div className="col-span-6 border border-emerald-200 rounded-md p-3 bg-emerald-50/40">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                      <Checkbox checked={pay.received} onCheckedChange={(v) => {
                        const grand = form.items.reduce((s, it) => s + (+it.quantity || 0) * (+it.unit_price || 0) * (1 + (+it.gst || 0) / 100), 0) + (+form.freight || 0) * 1.18;
                        const adv = advanceForSo(form.sales_order_id);
                        setPay({ ...pay, received: !!v, amount: v ? Math.max(grand - adv, 0).toFixed(2) : "" });
                      }} data-testid="inv-pay-received" />
                      Payment received for this invoice?
                    </label>
                    {advanceForSo(form.sales_order_id) > 0 && (
                      <p className="text-[11px] text-indigo-600 mt-1.5" data-testid="inv-advance-hint">
                        Advance of {inr(advanceForSo(form.sales_order_id))} already received (against this order, its pro forma, or on account) — it auto-adjusts on this invoice.
                        {pay.received && " Amount below is pre-filled with the balance AFTER the advance; enter only additional money received."}
                      </p>
                    )}
                    {pay.received && (
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount Received (₹)</Label>
                          <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 h-9" data-testid="inv-pay-amount" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mode</Label>
                          <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                            <SelectTrigger className="mt-1 h-9" data-testid="inv-pay-mode"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                              <SelectItem value="Cheque">Cheque</SelectItem>
                              <SelectItem value="UPI">UPI</SelectItem>
                              <SelectItem value="Cash">Cash</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reference (UTR / Cheque No.)</Label>
                          <Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="mt-1 h-9" data-testid="inv-pay-reference" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">E-Way Bill Helper — taxable vs total values</span>
                  <span className="text-sm font-mono font-semibold text-blue-700" data-testid="inv-number-display">Invoice No: {invNumber || "—"}</span>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inv-eway-helper">
                  <thead className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-100">
                    <tr>
                      <th className="text-left px-3 py-1.5">Product Description</th>
                      {consInfo && <th className="text-right px-3 py-1.5">Qty This Consignment</th>}
                      <th className="text-right px-3 py-1.5">Before GST</th>
                      <th className="text-right px-3 py-1.5">GST</th>
                      <th className="text-right px-3 py-1.5">After GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 && (
                      <tr><td colSpan={consInfo ? 5 : 4} className="px-3 py-3 text-center text-sm text-slate-500">Select a sales order to load products.</td></tr>
                    )}
                    {form.items.map((it, i) => {
                      const taxable = (+it.quantity || 0) * (+it.unit_price || 0);
                      const gstAmt = taxable * (+it.gst || 0) / 100;
                      const over = consInfo && it._max !== undefined && +it.quantity > it._max + 0.001;
                      return (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="px-3 py-1.5 text-slate-700">{it.description || "—"}</td>
                          {consInfo && (
                            <td className="px-3 py-1.5 text-right">
                              <Input type="number" value={it.quantity}
                                onChange={(e) => setForm((f) => ({ ...f, items: f.items.map((x, xi) => xi === i ? { ...x, quantity: e.target.value } : x) }))}
                                className={`h-8 w-28 text-right inline-block ${over ? "border-rose-400 text-rose-600" : ""}`}
                                data-testid={`inv-cons-qty-${i}`} />
                              <div className={`text-[10px] ${over ? "text-rose-600 font-medium" : "text-slate-400"}`}>
                                {over ? `max ${num(it._max)}!` : `of ${num(it._max)} remaining`}
                              </div>
                            </td>
                          )}
                          <td className="px-3 py-1.5 text-right tabular-nums">{inr(taxable)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{inr(gstAmt)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">{inr(taxable + gstAmt)}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50 font-semibold">
                      <td className="px-3 py-1.5" colSpan={consInfo ? 2 : 1}>Total{+form.freight > 0 ? " (+ freight)" : ""}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(totals.sub + (+form.freight || 0))}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(totals.gst + freightGst)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{inr(grand)}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>

              <div className="flex justify-end mt-2">
                <div className="w-full max-w-xs space-y-1 text-sm text-slate-600">
                  <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{inr(totals.sub)}</span></div>
                  <div className="flex justify-between"><span>GST{+form.freight > 0 ? " (incl. 18% on freight)" : ""}</span><span className="tabular-nums">{inr(totals.gst + freightGst)}</span></div>
                  {+form.freight > 0 && <div className="flex justify-between"><span>Freight (from Sales Order)</span><span className="tabular-nums">{inr(+form.freight)}</span></div>}
                  <div className="flex justify-between font-bold text-slate-900 border-t border-slate-300 pt-1"><span>Total</span><span className="tabular-nums">{inr(grand)}</span></div>
                </div>
              </div>

              </div>

              <DialogFooter className="shrink-0 border-t border-slate-100 pt-3 mt-1">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="inv-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <Input placeholder="Search invoice no., customer, e-way, status…" value={invSearch} onChange={(e) => setInvSearch(e.target.value)}
             className="max-w-md" data-testid="inv-search" />

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Number</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-left px-4 py-2.5 hidden lg:table-cell">Due</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-right px-4 py-2.5 hidden md:table-cell">Paid</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((i) => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`inv-row-${i.id}`}>
                <td className="px-4 py-3 font-mono">{i.number}</td>
                <td className="px-4 py-3">{i.customer_snapshot?.company_name}</td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(i.date)}</td>
                <td className="px-4 py-3 hidden lg:table-cell">{fmtDate(i.due_date)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(i.total)}</td>
                <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell text-emerald-700">{inr(i.paid)}</td>
                <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <a href={`${API_URL}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-blue-600 inline-flex" title="PDF" data-testid={`inv-pdf-${i.id}`}><FileText size={13} /></a>
                    <button onClick={() => shareWhatsApp(i)} className="p-1.5 text-emerald-600 hover:text-emerald-800 inline-flex" title="Share on WhatsApp" data-testid={`inv-whatsapp-${i.id}`}><MessageCircle size={13} /></button>
                    {i.eway_file_id && (
                      <button onClick={() => viewEwayFile(i.eway_file_id)} className="p-1.5 text-amber-600 hover:text-amber-800 inline-flex" title="View attached E-Way bill" data-testid={`inv-eway-file-${i.id}`}><Paperclip size={13} /></button>
                    )}
                    {i.status !== "Paid" && i.status !== "Returned" && (
                      <button onClick={() => paymentLink(i)} className="p-1.5 text-blue-600 hover:text-blue-800 inline-flex" title="Copy Razorpay payment link" data-testid={`inv-paylink-${i.id}`}><IndianRupee size={13} /></button>
                    )}
                    {i.status === "Overdue" && (
                      <button onClick={() => sendReminder(i)} disabled={remindBusy === i.id}
                              className="p-1.5 text-rose-600 hover:text-rose-800 inline-flex disabled:opacity-40"
                              title="Email overdue payment reminder" data-testid={`inv-remind-${i.id}`}>
                        <MailWarning size={13} />
                      </button>
                    )}
                    {i.status !== "Returned" && <button onClick={() => setAiEmailFor(i)} className="p-1.5 text-[#3B82F6] hover:text-[#2563EB] inline-flex" title="AI Email — draft & send invoice with PDF" data-testid={`inv-ai-email-${i.id}`}><Mail size={13} /></button>}
                    <button onClick={() => edit(i)} className="p-1.5 text-slate-500 hover:text-blue-600 inline-flex" title="Edit" data-testid={`inv-edit-${i.id}`}><Pencil size={13} /></button>
                    {i.status !== "Returned" && <button onClick={() => { setRetInv(i); setRetReopen(true); }} className="p-1.5 text-orange-500 hover:text-orange-700 inline-flex" title="Material rejected — mark Returned" data-testid={`inv-return-${i.id}`}><PackageX size={13} /></button>}
                    {i.status === "Returned" && <a href={`${API_URL}/invoices/${i.id}/credit-note`} target="_blank" rel="noreferrer" className="p-1.5 text-rose-600 hover:text-rose-800 inline-flex" title="Credit Note PDF" data-testid={`inv-credit-note-${i.id}`}><ReceiptText size={13} /></a>}
                    {i.status === "Returned" && <button onClick={() => openCaDocs(i)} className="p-1.5 text-indigo-600 hover:text-indigo-800 inline-flex" title="Documents for your CA — Credit Note, Invoice & Debit Note" data-testid={`inv-ca-docs-${i.id}`}><FolderOpen size={13} /></button>}
                    <button onClick={() => del(i.id)} className="p-1.5 text-slate-500 hover:text-rose-600 inline-flex" title="Delete" data-testid={`inv-del-${i.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {shownInv.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-500">{invSearch ? "No invoices match your search." : "No invoices yet."}</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={!!retInv} onOpenChange={(v) => !v && setRetInv(null)}>
        <DialogContent className="max-w-md" data-testid="inv-return-dialog">
          <DialogHeader>
            <DialogTitle>Material Rejected — Mark Invoice Returned</DialogTitle>
            <DialogDescription className="sr-only">Choose whether to reopen or close the linked sales order</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Invoice <b>{retInv?.number}</b> ({inr(retInv?.total || 0)}) will be marked <b>Returned</b>: excluded from
            Turnover, Profit and Outstanding, stock used will be added back, and a Credit Note PDF becomes available.
          </p>
          <RadioGroup value={retReopen ? "reopen" : "close"} onValueChange={(v) => setRetReopen(v === "reopen")} className="gap-2 mt-1">
            <label className="flex items-start gap-2.5 border border-slate-200 rounded-lg p-3 cursor-pointer has-checked:border-[#3B82F6]">
              <RadioGroupItem value="reopen" data-testid="ret-reopen" className="mt-0.5" />
              <span className="text-sm"><b>Reopen Sales Order</b><br /><span className="text-xs text-slate-500">Re-dispatch fresh material against the same order</span></span>
            </label>
            <label className="flex items-start gap-2.5 border border-slate-200 rounded-lg p-3 cursor-pointer has-checked:border-[#3B82F6]">
              <RadioGroupItem value="close" data-testid="ret-close" className="mt-0.5" />
              <span className="text-sm"><b>Close Sales Order</b><br /><span className="text-xs text-slate-500">No replacement will be sent</span></span>
            </label>
          </RadioGroup>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setRetInv(null)} data-testid="ret-cancel">Cancel</Button>
            <Button onClick={confirmReturn} disabled={retBusy} className="bg-[#C94A4A] hover:bg-[#b03e3e] text-white" data-testid="ret-confirm">
              {retBusy ? "Marking…" : "Mark Returned"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <InvoiceAiEmailDialog invoice={aiEmailFor} onClose={() => setAiEmailFor(null)} />

      <Dialog open={!!vcnInv} onOpenChange={(v) => !v && setVcnInv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{vcnDone ? "Documents for your CA" : "Upload Vendor Credit Note"}</DialogTitle>
            <DialogDescription className="sr-only">Record the vendor's credit note for the rejected material</DialogDescription>
          </DialogHeader>
          {!vcnDone ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 -mt-1">
                Material for <b>{vcnInv?.number}</b> was rejected and the order closed. Record the <b>credit note
                your vendor issued</b> for the returned material — it reduces what you owe on the vendor's bill.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Vendor CN Number</Label>
                  <Input value={vcn.number} onChange={(e) => setVcn({ ...vcn, number: e.target.value })}
                         placeholder="Vendor's credit note no." className="mt-1.5" data-testid="vcn-number" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Amount (INR) *</Label>
                  <Input type="number" value={vcn.amount} onChange={(e) => setVcn({ ...vcn, amount: e.target.value })} className="mt-1.5" data-testid="vcn-amount" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Credit Note File (PDF / photo)</Label>
                  <input type="file" accept=".pdf,image/*" className="hidden" id="vcn-file-input"
                         onChange={(e) => setVcnFile(e.target.files?.[0] || null)} data-testid="vcn-file-input" />
                  <button type="button" onClick={() => document.getElementById("vcn-file-input").click()}
                          className="mt-1.5 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5 h-9 text-slate-600 hover:border-slate-400 transition-colors duration-150 w-full" data-testid="vcn-upload-btn">
                    <Paperclip size={12} className="flex-none" />
                    <span className="truncate">{vcnFile ? vcnFile.name : "Upload vendor credit note"}</span>
                  </button>
                </div>
              </div>
              {vcnNeedsBill && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-3" data-testid="vcn-bill-section">
                  <p className="text-xs text-amber-800">No vendor bill is on record for this order. Enter the bill details — the CRM will create the bill and debit it.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-slate-500">Vendor Bill No. *</Label>
                      <Input value={vcnBill.number} onChange={(e) => setVcnBill({ ...vcnBill, number: e.target.value })}
                             placeholder="Bill / invoice no." className="mt-1.5 bg-white" data-testid="vcn-bill-number" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-slate-500">Bill Amount (INR) *</Label>
                      <Input type="number" value={vcnBill.amount} onChange={(e) => setVcnBill({ ...vcnBill, amount: e.target.value })} className="mt-1.5 bg-white" data-testid="vcn-bill-amount" />
                    </div>
                    <div className="col-span-2">
                      <input type="file" accept=".pdf,image/*" className="hidden" id="vcn-bill-file-input"
                             onChange={(e) => setVcnBillFile(e.target.files?.[0] || null)} data-testid="vcn-bill-file-input" />
                      <button type="button" onClick={() => document.getElementById("vcn-bill-file-input").click()}
                              className="inline-flex items-center gap-1.5 text-xs border border-amber-300 bg-white rounded-md px-2.5 h-9 text-slate-600 hover:border-amber-500 transition-colors duration-150 w-full" data-testid="vcn-bill-upload-btn">
                        <Paperclip size={12} className="flex-none" />
                        <span className="truncate">{vcnBillFile ? vcnBillFile.name : "Upload vendor bill (PDF / photo)"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setVcnInv(null)} data-testid="vcn-later">Later</Button>
                <Button onClick={saveVcn} disabled={vcnBusy} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vcn-save">
                  {vcnBusy ? "Saving…" : "Record Vendor Credit Note"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="vcn-ca-docs">
              <p className="text-xs text-slate-500 -mt-1">
                All CRM entries are done ({vcnDone.debit_note_number} recorded against the vendor bill).
                Send these documents to your CA for the GST records:
              </p>
              <div className="space-y-2">
                <a href={`${API_URL}/invoices/${vcnInv?.id}/credit-note`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 hover:border-[#3B82F6] hover:text-[#2563EB] transition-colors duration-150" data-testid="ca-doc-credit-note">
                  <ReceiptText size={14} /> 1. Credit Note to client (GST) — download PDF
                </a>
                <a href={`${API_URL}/invoices/${vcnInv?.id}/pdf`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 hover:border-[#3B82F6] hover:text-[#2563EB] transition-colors duration-150" data-testid="ca-doc-invoice">
                  <FileText size={14} /> 2. Original Tax Invoice {vcnInv?.number} — download PDF
                </a>
                <a href={`${API_URL}/vendors/bills/${vcnDone.bill_id}/debit-note-pdf`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-700 hover:border-[#3B82F6] hover:text-[#2563EB] transition-colors duration-150" data-testid="ca-doc-debit-note">
                  <ReceiptText size={14} /> 3. Debit Note to vendor {vcnDone.debit_note_number} — download PDF
                </a>
                {vcnDone.file_id && (
                  <p className="text-xs text-slate-500">4. The vendor's own credit note copy you uploaded is saved in the CRM (attached to the vendor bill).</p>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setVcnInv(null)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vcn-done-close">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

